import './models.scss';

import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import CONFIG from '../../../common/config';
import { KiaContext } from '../../../common/context/main.context';
import { formatPrice } from '../../../common/utils/price.utils';
import EasyMoveLegalBanner from '../../02_molecules/banner/EasyMoveLegalBanner';
import TaxDeductionBanner from '../../02_molecules/banner/TaxDeductionBanner';
import { InfoButton } from '../../02_molecules/powertrain/Powertrain';
import Icon from '../../01_atoms/icons/Icon';

export default function Models({ featured = false, isActive = false, setModelsActive }) {
    const isDropdown = featured === 'dropdown';
    const { cars, legal, prefix, currentLocale, bms, bmsExtended, isB2c, showMonthlyPrices } = useContext(KiaContext);
    const [activeTab, setActiveTab] = useState(isB2c ? 'all' : (!isDropdown ? 'ev' : 'all'));
    const [showFilterChangeHint, setShowFilterChangeHint] = useState(false);
    const hintLoopTimeoutRef = useRef(null);

    const clearHintLoopTimer = () => {
        if (hintLoopTimeoutRef.current != null) {
            window.clearTimeout(hintLoopTimeoutRef.current);
            hintLoopTimeoutRef.current = null;
        }
    };

    const scheduleHintShow = () => {
        clearHintLoopTimer();
        hintLoopTimeoutRef.current = window.setTimeout(() => {
            hintLoopTimeoutRef.current = null;
            setShowFilterChangeHint(true);
        }, 3000);
    };

    useEffect(() => {
        if (isB2c || isDropdown) {
            clearHintLoopTimer();
            setShowFilterChangeHint(false);
            return undefined;
        }
        scheduleHintShow();
        return () => clearHintLoopTimer();
    }, [isB2c, isDropdown]);

    useEffect(() => {
        if (activeTab !== 'all' && !(cars || []).some((car) => carHasEngineType(car, activeTab))) {
            setActiveTab('all');
        }
    }, [activeTab, cars]);

    /* 
        "ev" - Elektrisch
        "hev" - Hybride
        "ice_diesel" - Diesel
        "ice_sessence" - Benzine
        "mhev" - Mild Hybrid
        "phev" - Plugin hybride
    */
    const carHasEngineType = (car, type) => Array.isArray(car?.engine_types) && car.engine_types.some((engineType) => engineType === type);

    const tabs = [
        { id: 'all', messageId: 'models.tabs.all' },
        { id: 'ice_sessence', messageId: 'models.tabs.ice_sessence' },
        { id: 'ice_diesel', messageId: 'models.tabs.ice_diesel' },
        { id: 'mhev', messageId: 'models.tabs.mhev' },
        { id: 'hev', messageId: 'models.tabs.hev' },
        { id: 'phev', messageId: 'models.tabs.phev' },
        { id: 'ev', messageId: 'models.tabs.ev' },
    ].filter((tab) => tab.id === 'all' || (cars || []).some((car) => carHasEngineType(car, tab.id)));

    const getFilteredCars = (cars) => {
        if (activeTab === 'all') {
            return cars;
        }

        return cars.filter((car) => carHasEngineType(car, activeTab));
    };

    const filteredCars = getFilteredCars(cars);
    const carsType = filteredCars.filter(car => car.car_type === 'car');
    const vansType = filteredCars.filter(car => car.car_type === 'van');
    const gridColumns = useModelGridColumns();
    const isLastRow = (index, total) => index >= Math.floor((total - 1) / gridColumns) * gridColumns;

    // Determine title message ID based on BMS status
    const getTitleMessageId = () => {
        if (bmsExtended) {
            return "models.title.bms.extended";
        } else if (bms) {
            return "models.title.bms";
        } else {
            return "models.title";
        }
    };

    if (CONFIG.DEBUG) {
        console.log('carsType', carsType);
        console.log('vansType', vansType);
    }

    return (
        <>
        <div className={`models ${featured === "dropdown" ? 'models--featured--dropdown' : ''} ${isActive ? 'models--active' : ''} ${isB2c ? 'models--b2c' : 'models--b2b'}`}>
            {isDropdown ? <TaxDeductionBanner /> : null}

            <div className='models_inner'>
                <h2 className={isB2c ? 'models-title-b2c' : ''}>
                    <FormattedMessage id={getTitleMessageId()} />
                </h2>
                {!isB2c && (
                    <p className='models-subtitle'><FormattedMessage id="models.subtitle" /></p>
                )}

                {/* Tabs */}
                <div className='models-tabs-container'>
                    <div className='models-tabs'>
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type='button'
                                className={`models-tab ${activeTab === tab.id ? 'models-tab--active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                <FormattedMessage id={tab.messageId} />
                            </button>
                        ))}
                    </div>
                    {!isB2c && !isDropdown && showFilterChangeHint && (
                        <div
                            className='models-filter-hint-popup'
                            onAnimationEnd={(e) => {
                                if (e.target !== e.currentTarget) return;
                                if (!String(e.animationName || '').includes('models-filter-hint-lifecycle')) return;
                                if (isB2c || isDropdown) return;
                                setShowFilterChangeHint(false);
                                scheduleHintShow();
                            }}
                        >
                            <div className='models-filter-hint-popup__body'>
                                <p className='models-filter-hint-popup__text'>
                                    <FormattedMessage
                                        id='models.filter.change.hint'
                                        defaultMessage='Discover all models.'
                                    />
                                </p>
                                <div className='models-filter-hint-popup__progress' aria-hidden>
                                    <div className='models-filter-hint-popup__progress-bar' />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {carsType.length > 0 && (
                    <div className='list-models-section'>
                        {
                            vansType.length > 0 &&
                            <h3><FormattedMessage id="models.cars.type.title" /></h3>
                        }
                        <div className='list-models'>
                            {carsType.map((car, index) => (
                                <ModelCard
                                    key={car.id}
                                    car={car}
                                    prefix={prefix}
                                    setModelsActive={setModelsActive}
                                    isB2c={isB2c}
                                    currentLocale={currentLocale}
                                    showMonthlyPrices={showMonthlyPrices}
                                    showNewLogo
                                    positionRight={(index + 1) % gridColumns === 0}
                                    positionTop={vansType.length === 0 && isLastRow(index, carsType.length)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {vansType.length > 0 && (
                    <div className="list-models-section">
                        {
                            carsType.length > 0 &&
                            <h3><FormattedMessage id="models.vans.type.title" /></h3>
                        }
                        <div className='list-models'>
                            {vansType.map((car, index) => (
                                <ModelCard
                                    key={car.id}
                                    car={car}
                                    prefix={prefix}
                                    setModelsActive={setModelsActive}
                                    isB2c={isB2c}
                                    currentLocale={currentLocale}
                                    showMonthlyPrices={showMonthlyPrices}
                                    positionRight={(index + 1) % gridColumns === 0}
                                    positionTop={isLastRow(index, vansType.length)}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Legal */}
                <div className='menu-legal' dangerouslySetInnerHTML={{ __html: legal.homepagemenu }} />
                {showMonthlyPrices && isB2c ? (
                    <>
                        <div className="car__legal__devider mt--4" />
                        <h4 className="heading fs--h3 pt--4 pb--12 fc--secondary"><FormattedMessage id="legal.attention" /></h4>
                    </>
                ) : null}
            </div>
        </div>
        {isDropdown && isActive && ReactDOM.createPortal(<EasyMoveLegalBanner />, document.body)}
        </>
    );
}

function useModelGridColumns() {
    const [columns, setColumns] = useState(6);

    useEffect(() => {
        const updateColumns = () => {
            const width = window.innerWidth;
            if (width <= 576) setColumns(2);
            else if (width <= 820) setColumns(3);
            else if (width <= 1024) setColumns(4);
            else if (width <= 1520) setColumns(5);
            else setColumns(6);
        };

        updateColumns();
        window.addEventListener('resize', updateColumns);
        return () => window.removeEventListener('resize', updateColumns);
    }, []);

    return columns;
}

function getPopupClassName(positionRight, positionTop) {
    return [positionRight && 'position-right', positionTop && 'position-top'].filter(Boolean).join(' ');
}

function ModelCard({ car, prefix, setModelsActive, isB2c, currentLocale, showMonthlyPrices, showNewLogo = false, positionRight = false, positionTop = false }) {
    const to = `${prefix}/cars/${car.slug}`;
    const closeMenu = () => {
        if (setModelsActive) {
            setModelsActive(false);
        }
    };

    return (
        <div className={`car-item${positionRight ? ' position-right' : ''}${positionTop ? ' position-top' : ''}`}>
            <Link to={to} className='car-item__link' onClick={closeMenu}>
                <div className='car-image'>
                    {showNewLogo && car.show_new_logo && (
                        <span className='car-is-new'><FormattedMessage id="car.is.new" /></span>
                    )}
                    <img src={car.thumbnail} alt={car.title} />
                </div>
            </Link>
            <div className='car-info'>
                <h3>
                    <Link to={to} onClick={closeMenu}>{car.price_default_short_name}</Link>
                </h3>
                <ModelCardPrices {...{ car, isB2c, currentLocale, showMonthlyPrices, positionRight, positionTop }} />
            </div>
        </div>
    );
}

function ModelCardPrices({ car, isB2c, currentLocale, showMonthlyPrices, positionRight = false, positionTop = false }) {
    const locale = currentLocale?.locale;
    const easymovePrice = Number(car?.easymove?.price || car?.price_easymove);
    const lastPayment = Number(car?.easymove?.amount_last_payment || car?.amount_last_payment);
    const financialRentingPrice = Number(car?.financial_renting?.price || car?.price_financial_renting);
    const hasEasyMove = isB2c && showMonthlyPrices && easymovePrice > 0;
    const hasLastPayment = hasEasyMove && lastPayment > 0;
    const hasFinancialRenting = !isB2c && showMonthlyPrices && financialRentingPrice > 0;

    return (
        <>
            {car.price_motor_show > 0 && (
                <div className='car-price'>
                    <span className='price-label'>
                        <FormattedMessage
                            id="price.from"
                            values={{
                                price: <span className="bold car-price__catalog-amount">{formatPrice(car.price_motor_show, CONFIG.PRICE_FORMAT, locale)}</span>,
                            }}
                        />
                    </span>
                </div>
            )}
            {hasEasyMove && (
                <div className='car-price car-price--easymove'>
                    <FormattedMessage
                        id="models.easymove.from"
                        values={{
                            price: (
                                <>
                                    <span className="bold car-price__easymove-amount">
                                        {formatPrice(easymovePrice, CONFIG.PRICE_FORMAT, locale)}
                                    </span>
                                    <span className="bold"> / <FormattedMessage id="powertrain.month" /></span>
                                </>
                            ),
                            info: <ModelEasyMoveInfo car={car} locale={locale} positionRight={positionRight} positionTop={positionTop} />,
                        }}
                    />
                </div>
            )}
            {hasLastPayment && (
                <div className='car-price car-price--easymove-last'>
                    <FormattedMessage
                        id="easymove.amount_last_payment"
                        values={{
                            amount_last_payment: (
                                <span className="bold car-price__last-payment">
                                    {formatPrice(lastPayment, CONFIG.PRICE_FORMAT_LEGAL, locale)}
                                </span>
                            ),
                        }}
                    />
                </div>
            )}
            {hasFinancialRenting && (
                <div className='car-price car-price--financial-renting'>
                    <FormattedMessage
                        id="models.financial_renting.from"
                        values={{
                            price: (
                                <span className="bold">
                                    <span className="bold car-price__financial-renting-amount">
                                        {formatPrice(financialRentingPrice, CONFIG.PRICE_FORMAT, locale)}
                                    </span>
                                    {' / '}
                                    <FormattedMessage id="powertrain.month" />
                                </span>
                            ),
                            info: <ModelFinancialRentingInfo car={car} locale={locale} positionRight={positionRight} positionTop={positionTop} />,
                        }}
                    />
                </div>
            )}
        </>
    );
}

function getModelEasyMoveCar(car) {
    const engines = Array.isArray(car?.engine) ? car.engine : [];
    const infoCar =
        engines.find((engine) => engine.is_exposed_car && Number(engine?.easymove?.price) > 0) ||
        engines.find((engine) => Number(engine?.easymove?.price) > 0) ||
        engines.find((engine) => engine.is_exposed_car) ||
        null;

    if (infoCar?.easymove) {
        return infoCar;
    }

    return {
        ...car,
        title: car?.title,
        power: car?.power,
        price: car?.price,
        price_trade_bonus: car?.price_trade_bonus,
        easymove: {
            price: Number(car?.easymove?.price || car?.price_easymove) || null,
            amount_to_finance: car?.easymove?.amount_to_finance || car?.amount_loan || null,
            amount_deposit: car?.easymove?.amount_deposit || car?.amount_deposit || null,
            amount_cash: car?.easymove?.amount_cash || car?.amount_cash || null,
            amount_jkp: car?.easymove?.amount_jkp || car?.amount_jkp || null,
            amount_intrest: car?.easymove?.amount_intrest || car?.amount_intrest || null,
            amount_term: car?.easymove?.amount_term || car?.amount_terms || null,
            amount_last_payment: car?.easymove?.amount_last_payment || car?.amount_last_payment || null,
            amount_total: car?.easymove?.amount_total || car?.amount_total || null,
            is_electrical: car?.easymove?.is_electrical || false,
        },
    };
}

function ModelEasyMoveInfo({ car, locale, positionRight = false, positionTop = false }) {
    const infoCar = getModelEasyMoveCar(car);

    if (!infoCar?.easymove) {
        return <Icon icon="alert" />;
    }

    return (
        <span
            className="car-price__easymove-info"
            onClick={(e) => {
                e.stopPropagation();
                if (!e.target.closest('.popup')) {
                    e.preventDefault();
                }
            }}
        >
            <InfoButton
                car={infoCar}
                promo_information={infoCar.promo_information || car.price_promo_information || car.price_default_promo_information}
                price_discount={infoCar.price_discount ?? car.price_discount}
                price_extra_advantage_label={infoCar.price_extra_advantage_label ?? car.price_extra_advantage_label}
                price_extra_advantage={infoCar.price_extra_advantage ?? car.price_extra_advantage}
                price_bonus={infoCar.price_bonus ?? car.price_bonus}
                price_net={infoCar.price_net ?? car.price_net}
                price_motor_show={infoCar.price_motor_show ?? car.price_motor_show}
                price_trade_bonus={infoCar.price_trade_bonus ?? car.price_trade_bonus}
                recycle_bonus={infoCar.recycle_bonus ?? car.recycle_bonus}
                model={car.slug}
                locale={locale}
                isB2c
                type="easymove"
                popupClassName={getPopupClassName(positionRight, positionTop)}
            />
        </span>
    );
}

function getModelFinancialRentingCar(car) {
    const engines = Array.isArray(car?.engine) ? car.engine : [];
    const infoCar =
        engines.find((engine) => engine.is_exposed_car && Number(engine?.financial_renting?.price) > 0) ||
        engines.find((engine) => engine.is_exposed_car) ||
        null;

    if (infoCar?.financial_renting) {
        return infoCar;
    }

    return {
        ...car,
        title: car?.title,
        power: car?.power,
        financial_renting: car?.financial_renting || {
            price: Number(car?.price_financial_renting) || null,
        },
    };
}

function ModelFinancialRentingInfo({ car, locale, positionRight = false, positionTop = false }) {
    const infoCar = getModelFinancialRentingCar(car);

    if (!infoCar?.financial_renting) {
        return <Icon icon="alert" />;
    }

    return (
        <span
            className="car-price__easymove-info"
            onClick={(e) => {
                e.stopPropagation();
                if (!e.target.closest('.popup')) {
                    e.preventDefault();
                }
            }}
        >
            <InfoButton
                car={infoCar}
                promo_information={infoCar.promo_information || car.price_promo_information || car.price_default_promo_information}
                price_discount={infoCar.price_discount ?? car.price_discount}
                price_extra_advantage_label={infoCar.price_extra_advantage_label ?? car.price_extra_advantage_label}
                price_extra_advantage={infoCar.price_extra_advantage ?? car.price_extra_advantage}
                price_bonus={infoCar.price_bonus ?? car.price_bonus}
                price_net={infoCar.price_net ?? car.price_net}
                price_motor_show={infoCar.price_motor_show ?? car.price_motor_show}
                price_trade_bonus={infoCar.price_trade_bonus ?? car.price_trade_bonus}
                recycle_bonus={infoCar.recycle_bonus ?? car.recycle_bonus}
                model={car.slug}
                locale={locale}
                isB2c={false}
                type="financial_renting"
                popupClassName={getPopupClassName(positionRight, positionTop)}
            />
        </span>
    );
}